/**
 * Google Login Button Component
 *
 * Renders a styled button that triggers Google OAuth sign-in.
 * Uses Google Identity Services (GIS) via script tag injection.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../ui";
import { getConfig } from "../config/runtime-config";

declare global {
  interface Window {
    readonly google?: {
      readonly accounts: {
        readonly id: {
          readonly initialize: (config: GoogleIdConfig) => void;
          readonly renderButton: (
            element: HTMLElement,
            options: GoogleButtonOptions,
          ) => void;
        };
      };
    };
  }
}

interface GoogleIdConfig {
  readonly client_id: string;
  readonly callback: (response: GoogleCredentialResponse) => void;
  readonly auto_select?: boolean;
}

interface GoogleButtonOptions {
  readonly type: "standard" | "icon";
  readonly theme: "outline" | "filled_blue" | "filled_black";
  readonly size: "large" | "medium" | "small";
  readonly width?: number;
  readonly text?: "signin_with" | "signup_with" | "continue_with";
}

interface GoogleCredentialResponse {
  readonly credential: string;
}

interface GoogleLoginButtonProps {
  readonly onSuccess: (idToken: string) => void;
  readonly onError?: () => void;
  readonly text?: "signin_with" | "signup_with";
  readonly disabled?: boolean;
}

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function isGisLoaded(): boolean {
  return typeof window.google?.accounts?.id !== "undefined";
}

export function GoogleLoginButton({
  onSuccess,
  onError,
  text = "signin_with",
  disabled = false,
}: GoogleLoginButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(isGisLoaded);
  const [scriptError, setScriptError] = useState(false);

  const handleCredentialResponse = useCallback(
    (response: GoogleCredentialResponse) => {
      if (response.credential) {
        onSuccess(response.credential);
      } else {
        onError?.();
      }
    },
    [onSuccess, onError],
  );

  // Load GIS script
  useEffect(() => {
    if (isGisLoaded()) {
      setScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector(
      `script[src="${GIS_SCRIPT_SRC}"]`,
    );
    if (existingScript) {
      existingScript.addEventListener("load", () => setScriptLoaded(true));
      existingScript.addEventListener("error", () => setScriptError(true));
      return;
    }

    const script = document.createElement("script");
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => setScriptError(true);
    document.head.appendChild(script);
  }, []);

  // Initialize and render Google button
  useEffect(() => {
    if (!scriptLoaded || !buttonRef.current || disabled) return;

    const clientId = getConfig().googleOAuthClientId;
    if (!clientId) return;

    window.google?.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredentialResponse,
    });

    window.google?.accounts.id.renderButton(buttonRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      width: buttonRef.current.offsetWidth,
      text,
    });
  }, [scriptLoaded, handleCredentialResponse, text, disabled]);

  const clientId = getConfig().googleOAuthClientId;
  if (!clientId) {
    return null;
  }

  if (scriptError) {
    return (
      <Button variant="secondary" disabled className="w-full opacity-60">
        Google 로그인을 사용할 수 없습니다
      </Button>
    );
  }

  return (
    <div
      ref={buttonRef}
      data-testid="google-login-btn"
      className={disabled ? "pointer-events-none opacity-60" : ""}
    />
  );
}
